import React, { useEffect } from 'react';
import { Grid, TextField, MenuItem } from '@mui/material';
import { useForm } from '@refinedev/react-hook-form';
import { HttpError, useList } from '@refinedev/core';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { StudySearchFilters } from '../hooks/useStudiesList';
import { StudyCategory } from '@aphp-ema/common/study';
import { TRANSLATION_KEYS } from '../constants/translationKeys';

type StudyFiltersProps = {
  onSearch: (filters: StudySearchFilters) => void;
  defaultFilters?: StudySearchFilters;
  debounceMs?: number;
};

export const StudyFilters: React.FC<StudyFiltersProps> = ({
  onSearch,
  defaultFilters = {},
  debounceMs = 300,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, subscribe } = useForm<
    StudySearchFilters,
    HttpError
  >({
    defaultValues: defaultFilters,
    warnWhenUnsavedChanges: false,
  });

  const { data: categories, isLoading: categoriesLoading } =
    useList<StudyCategory>({
      resource: 'admin/studies/categories',
      pagination: {
        mode: 'off',
      },
    });

  const debouncedSearch = useDebounce(onSearch, debounceMs);

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        debouncedSearch(values as StudySearchFilters);
      },
    });
    return () => unsubscribe();
  }, [subscribe, debouncedSearch]);

  const statusOptions = [
    { value: '', label: t(TRANSLATION_KEYS.SEARCH.ALL_STATUSES) },
    { value: 'draft', label: t(TRANSLATION_KEYS.STATUS.DRAFT) },
    { value: 'published', label: t(TRANSLATION_KEYS.STATUS.PUBLISHED) },
  ];

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSearch)}
      sx={{ mb: 2, pt: 1 }}
    >
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          {...register('name')}
          fullWidth
          label={t(TRANSLATION_KEYS.FIELDS.NAME)}
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          {...register('contactName')}
          fullWidth
          label={t(TRANSLATION_KEYS.FIELDS.CONTACT_NAME)}
          size="small"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          {...register('categoryId')}
          select
          fullWidth
          label={t(TRANSLATION_KEYS.FIELDS.CATEGORY)}
          size="small"
          disabled={categoriesLoading}
          defaultValue=""
        >
          <MenuItem value="">
            {t(TRANSLATION_KEYS.SEARCH.ALL_CATEGORIES)}
          </MenuItem>
          {categories?.data.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          {...register('status')}
          select
          fullWidth
          label={t(TRANSLATION_KEYS.FIELDS.STATUS)}
          size="small"
          defaultValue=""
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
};
